const PLACEHOLDER_PATTERN = /\{([^{}]+)\}/g;

export const getVarStoreReferences = expression => {
  const references = [];

  String(expression ?? "").replace(PLACEHOLDER_PATTERN, (_match, fieldName) => {
    const normalizedName = fieldName.trim();

    if (normalizedName && !references.includes(normalizedName)) {
      references.push(normalizedName);
    }

    return _match;
  });

  return references;
};

const replacePlaceholders = (value, values) =>
  String(value ?? "").replace(PLACEHOLDER_PATTERN, (_match, fieldName) => {
    const resolvedValue = values?.[fieldName.trim()];
    return resolvedValue === undefined || resolvedValue === null
      ? ""
      : String(resolvedValue);
  });

const findUnquotedDelimiter = (value, delimiter) => {
  let quote = null;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if ((character === "'" || character === '"') && value[index - 1] !== "\\") {
      quote = quote === character ? null : quote || character;
    } else if (!quote && character === delimiter) {
      return index;
    }
  }

  return -1;
};

const resolveToken = (token, values) => {
  const trimmedToken = String(token ?? "").trim();
  const isQuoted =
    trimmedToken.length >= 2 &&
    ((trimmedToken.startsWith("'") && trimmedToken.endsWith("'")) ||
      (trimmedToken.startsWith('"') && trimmedToken.endsWith('"')));
  const unquotedToken = isQuoted
    ? trimmedToken.slice(1, -1)
    : trimmedToken;

  return replacePlaceholders(unquotedToken, values);
};

const toComparableValue = value => {
  const normalizedValue = String(value ?? "").trim();

  if (normalizedValue !== "" && Number.isFinite(Number(normalizedValue))) {
    return Number(normalizedValue);
  }

  return normalizedValue;
};

const compareValues = (leftValue, operator, rightValue) => {
  const left = toComparableValue(leftValue);
  const right = toComparableValue(rightValue);

  switch (operator) {
    case "==":
      return left === right;
    case "!=":
      return left !== right;
    case ">":
      return left > right;
    case "<":
      return left < right;
    case ">=":
      return left >= right;
    case "<=":
      return left <= right;
    default:
      return false;
  }
};

const parseConditionalExpression = expression => {
  const conditionMatch = String(expression ?? "").match(
    /^\s*\{([^{}]+)\}\s*(==|!=|>=|<=|>|<)\s*/
  );

  if (!conditionMatch) return null;

  const remainingExpression = expression.slice(conditionMatch[0].length);
  const questionIndex = findUnquotedDelimiter(remainingExpression, "?");

  if (questionIndex < 0) return null;

  const resultExpression = remainingExpression.slice(questionIndex + 1);
  const colonIndex = findUnquotedDelimiter(resultExpression, ":");

  if (colonIndex < 0) return null;

  return {
    fieldName: conditionMatch[1].trim(),
    operator: conditionMatch[2],
    comparisonToken: remainingExpression.slice(0, questionIndex),
    trueToken: resultExpression.slice(0, colonIndex),
    falseToken: resultExpression.slice(colonIndex + 1)
  };
};

export const resolveVarStoreExpression = (expression, values = {}) => {
  const conditionalExpression = parseConditionalExpression(expression);

  if (!conditionalExpression) {
    return replacePlaceholders(expression, values);
  }

  const {
    fieldName,
    operator,
    comparisonToken,
    trueToken,
    falseToken
  } = conditionalExpression;
  const comparisonValue = resolveToken(comparisonToken, values);
  const conditionMatches = compareValues(
    values?.[fieldName],
    operator,
    comparisonValue
  );

  return resolveToken(conditionMatches ? trueToken : falseToken, values);
};
